import { yupResolver } from '@hookform/resolvers/yup'
import CancelIcon from '@mui/icons-material/Cancel'
import ModeEditIcon from '@mui/icons-material/ModeEdit'
import SaveAsIcon from '@mui/icons-material/SaveAs'
import { LoadingButton } from '@mui/lab'
import { Box, Button, Stack, TextField, Typography } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { format } from 'date-fns'
import { useSnackbar } from 'notistack'
import { useEffect, useRef, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import axiosClient from 'utils/axiosClient'
import * as yup from 'yup'

type Props = {
  apiEndpoint?: string
  label: string
  startDate: number
  endDate: number
}
interface FormInput {
  startDate: number
  endDate: number
}

export default function EditDateRange({
  apiEndpoint,
  startDate,
  endDate,
  label,
}: Props) {
  const { enqueueSnackbar } = useSnackbar()
  const schema = yup.object({
    startDate: yup.number().required(),
    endDate: yup.number().required(),
  })
  const {
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
  } = useForm<FormInput>({
    resolver: yupResolver(schema),
    defaultValues: {
      startDate,
      endDate,
    },
  })
  const [loading, setLoading] = useState(false)
  const [edit, setEdit] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)
  const handleKeyDown = (e: globalThis.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setEdit(false)
      setValue('startDate', startDate, {
        shouldValidate: true,
      })
      setValue('endDate', endDate, {
        shouldValidate: true,
      })
    }
  }
  useEffect(() => {
    if (ref.current) {
      if (edit) {
        ref.current.addEventListener('keydown', (e) => {
          handleKeyDown(e)
        })
      } else {
        ref.current.removeEventListener('keydown', (e) => {
          handleKeyDown(e)
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edit])
  const onSubmit: SubmitHandler<FormInput> = async (data) => {
    if (apiEndpoint) {
      setLoading(true)
      try {
        await axiosClient.put(apiEndpoint, {
          data: JSON.stringify(data),
        })
        setEdit(false)
        const msg = 'イベントの' + label + 'を修正しました。'
        enqueueSnackbar(msg, {
          variant: 'success',
        })
        setLoading(false)
      } catch (_error) {
        const msg = 'エラーが発生しました。'
        enqueueSnackbar(msg, {
          variant: 'error',
        })
      }
    }
  }
  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} my={2}>
      {edit ? (
        <Stack
          direction={{ md: 'row', xs: 'column' }}
          alignItems="center"
          sx={{ display: edit ? 'flex' : 'none' }}
          ref={ref}
          spacing={2}
        >
          <Stack
            direction={{ md: 'row', xs: 'column' }}
            flex={1}
            alignItems="center"
            sx={{ width: '100%' }}
          >
            <DatePicker
              label="start"
              inputFormat="yyyy/MM/dd"
              value={new Date(getValues('startDate'))}
              onChange={(date, keyboardInputValue) => {
                if (
                  !keyboardInputValue &&
                  date &&
                  date instanceof Date &&
                  !isNaN(date.valueOf())
                ) {
                  setValue('startDate', date.getTime(), {
                    shouldValidate: true,
                  })
                  if (date.getTime() > getValues('endDate')) {
                    setValue('endDate', date.getTime(), {
                      shouldValidate: true,
                    })
                  }
                }
              }}
              renderInput={(params) => <TextField {...params} />}
            />
            <Typography
              variant="body2"
              component="p"
              sx={{ width: 60 }}
              my={2}
              textAlign="center"
            >
              から
            </Typography>
            <DatePicker
              label="end"
              minDate={new Date(getValues('startDate'))}
              inputFormat="yyyy/MM/dd"
              value={new Date(getValues('endDate'))}
              onChange={(date, keyboardInputValue) => {
                if (
                  !keyboardInputValue &&
                  date &&
                  date instanceof Date &&
                  !isNaN(date.valueOf())
                ) {
                  setValue('endDate', date.getTime(), {
                    shouldValidate: true,
                  })
                }
              }}
              renderInput={(params) => <TextField {...params} />}
            />
          </Stack>
          <Stack
            spacing={4}
            direction={{ md: 'column', xs: 'row-reverse' }}
            flex={'none'}
          >
            <Button
              sx={{
                px: 1,
                minWidth: 0,
              }}
              size="small"
              color="error"
              variant="contained"
              onClick={() => {
                setEdit(false)
                setValue('startDate', startDate, {
                  shouldValidate: true,
                })
                setValue('endDate', endDate, {
                  shouldValidate: true,
                })
              }}
            >
              <CancelIcon />
            </Button>
            <LoadingButton
              disabled={Object.keys(errors).length > 0}
              loading={loading}
              sx={{
                px: 1,
                minWidth: 0,
              }}
              size="small"
              variant="contained"
              type="submit"
            >
              <SaveAsIcon />
            </LoadingButton>
          </Stack>
        </Stack>
      ) : (
        <Stack
          direction={{ md: 'row', xs: 'column' }}
          spacing={2}
          sx={{ display: !edit ? 'flex' : 'none' }}
          alignItems={{
            md: 'center',
            xs: 'flex-start',
          }}
        >
          <Typography fontWeight="bold" variant="body2" flex={'none'}>
            {label}
          </Typography>
          <Stack direction="row" alignItems="center">
            <Typography>
              {format(startDate, 'yyyy/MM/dd')} ～{' '}
              {format(endDate, 'yyyy/MM/dd')}
            </Typography>
            <Button
              sx={{
                flex: 'none',
                mx: 1,
                p: 0.5,
                minWidth: 0,
              }}
              size="small"
              variant="contained"
              color="info"
              onClick={() => {
                setEdit(true)
              }}
            >
              <ModeEditIcon fontSize="small" />
            </Button>
          </Stack>
        </Stack>
      )}
    </Box>
  )
}
